<script setup lang="ts">
const { t } = useI18n()

const { searchParams } = useSearchParams()

const nuxtApp = useNuxtApp()
const { data: collection, pending: collectionLoading } = await useLazyAsyncData(`collection:${searchParams.value.collectionId}`, async () =>
  useGraphqlQuery('collection', { id: searchParams.value.collectionId }).then(result => result.data.collection), {
  getCachedData: key => nuxtApp.payload.data[key] || nuxtApp.static.data[key],
})

onMounted(() => {
  window.scrollTo(0, 0)
})

const title = computed(() => t('layouts.title', [collection.value?.name ?? t('pages.title.index')]))
useSeoMeta({
  title,
  ogTitle: title,
})
</script>

<template>
  <div>
    <section id="page-breadcrumbs" class="bg-slate-50">
      <div class="mx-auto max-w-3xl px4 py2 lg:max-w-[88rem] md:max-w-4xl lg:px6 sm:px4 sm:px6">
        <ProductBreadcrumbs
          :items="collection?.breadcrumbs?.map((crumb) => ({
            ...crumb,
            path: `/collections/${crumb.id}/${crumb.slug}`,
          }))"
        />
      </div>
    </section>

    <div class="mx-auto max-w-3xl px4 py2 lg:max-w-[88rem] md:max-w-4xl lg:px6 sm:px4 sm:px6">
      <h2 n="2xl slate9 dark:slate2" class="p fw500 tracking-tight">
        <NSkeleton v-if="collectionLoading || !collection" class="w48" />
        <template v-else>
          {{ collection.name }}
        </template>
      </h2>

      <section
        v-if="collection?.children && collection.children.length > 0"
        id="collection-children"
        class="mx-auto"
      >
        <h2 class="sr-only">
          {{ t('collections.page.title') }}
        </h2>

        <div class="mx-auto flex gap-x-6 gap-y-4 overflow-x-auto pt3">
          <template v-if="!collection?.children">
            <div>
              <NSkeleton class="h18 w18 sm:h28 sm:w28" shape="circle" pulse />
            </div>
            <NSkeleton />
          </template>

          <ul
            v-else
            class="grid auto-cols-[minmax(9rem,_1fr)] grid-flow-col grid-rows-2 w-max gap-2 overflow-x-auto pb-2 lg:auto-cols-auto lg:grid-flow-row lg:grid-cols-6 lg:grid-rows-none lg:w-full lg:overflow-visible"
          >
            <li
              v-for="child in collection.children"
              :key="child.id"
              class="shrink-0"
            >
              <CollectionLink :collection="child" />
            </li>
          </ul>
        </div>
      </section>
    </div>

    <div class="mx-auto max-w-3xl px4 lg:max-w-[88rem] md:max-w-4xl lg:px6 sm:px4 sm:px6">
      <Products />
    </div>
  </div>
</template>
